import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { capitalize } from "lodash";
import moment from "moment";
import { FC, useCallback, useEffect, useState } from "react";
import { ActionIcon, Switch, Tooltip } from "rizzui";
import { toast } from "sonner";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import CustomTable from "../../../components/table/CustomTable";
import useFetch from "../../../hooks/useFetch";
import Page from "../../../layout/Page/Page";
import { ProviderApiResponse } from "../../../type/entities/provider-type";
import { Headquarter, HeadquarterApiResponse } from "../../../type/entities/headquarter-type";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import { HeadquarterService } from "../../../services/headquarters/headquarterService";
import HeadquartersFilters from "./HeadquarterFilters";
import HeadquarterForm from "../components/HeadquarterForm";
import { Link } from "react-router-dom";
import { menuRoutes } from "../../../router/menu";
import { HiOutlineEye } from "react-icons/hi2";
import useHandleErrors from "../../../hooks/useHandleErrors";
import HeadquarterDeleteAllModal from "../components/HeadquarterDeleteAllModal";

interface HeadquartersListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    headquarterSelected: Headquarter | null;
    setHeadquarterSelected: React.Dispatch<React.SetStateAction<Headquarter | null>>;
    openDeleteAllModal: boolean;
    setOpenDeleteAllModal: React.Dispatch<React.SetStateAction<boolean>>;
    registerTableRefetch?: (refetch: () => void) => void;
}

const columnOrder = [
    'id',
    'company',
    'name',
    'address',
    'city',
    'neighbourhood',
    'zipCode',
    'province',
    'contactName',
    'mobilePhone',
    'phone',
    'email',
    'pickBetweenDays',
    'highSeason',
    'startService',
    'endService',
    'gratification',
    'comments',
    'provider',
    'active',
    'createdAt',
    'updatedAt',
    'highSeasonDateFrom',
    'highSeasonDateTo',
    'coordinates',
    'dayOff',
    'pickupSchedule',
    'highSeasonDates'
];

const HeadquartersList: FC<HeadquartersListProps> = ({ openFormDrawer, setOpenFormDrawer, headquarterSelected, setHeadquarterSelected, openDeleteAllModal, setOpenDeleteAllModal, registerTableRefetch }) => {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const service = new HeadquarterService();

    // HOOKS

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { handleErrors } = useHandleErrors();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE SUCURSALES
     * @EN GETS THE LIST OF HEADQUARTERS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listHeadquarters(filters);
        return response.getResponseData() as HeadquarterApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES TOGGLE STATUS DE LA SEDE
     * @EN TOGGLE HEADQUARTER STATUS
     */
    //-------------------------------------------------------------------------------------------------------------
    const toggleStatus = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleHeadquarterStatus(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(response.message);
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA VARIAS SEDES
     * @EN DELETES MULTIPLE HEADQUARTERS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = await (await service.deleteMultiHeadquarter(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success(response.message);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        registerTableRefetch?.(refetch);
    }, [registerTableRefetch, refetch]);

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'headquarters-table'}
                    columnOrder={columnOrder}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    data={orderedData()}
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={[
                        'provider',
                        'active'
                    ]}
                    columnsNotShown={[
                        'highSeasonDateFrom',
                        'highSeasonDateTo',
                        'coordinates',
                        'dayOff',
                        'pickupSchedule',
                        'highSeasonDates',
                        'startService',
                        'endService',
                    ]}
                    overrideColumns={[
                        {
                            key: 'company',
                            columnNameKey: 'Empresa',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                if (!row.row.original?.company) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.company?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'name',
                            columnNameKey: 'Nombre',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'address',
                            columnNameKey: 'Dirección',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.address}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'city',
                            columnNameKey: 'Ciudad',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.city}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'neighbourhood',
                            columnNameKey: 'Barrio',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.neighbourhood || 'N/A'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'zipCode',
                            columnNameKey: 'C.P.',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.zipCode || 'N/A'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'contactName',
                            columnNameKey: 'Contacto',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.contactName || 'N/A'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'mobilePhone',
                            columnNameKey: 'Teléfono',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.mobilePhone || 'N/A'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'phone',
                            columnNameKey: 'Teléfono Fijo',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.phone || 'N/A'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'email',
                            columnNameKey: 'Email',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.email || row.row.original?.provider?.email || 'N/A'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'startService',
                            columnNameKey: 'Inicio Servicio',
                            render: (row: any) => {
                                const date = row.row.original?.startService?.date || row.row.original?.startService;
                                return (
                                    <div>
                                        {date ? moment(date).format('DD/MM/YYYY') : 'N/A'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'endService',
                            columnNameKey: 'Fin Servicio',
                            render: (row: any) => {
                                const date = row.row.original?.endService?.date || row.row.original?.endService;
                                return (
                                    <div>
                                        {date ? moment(date).format('DD/MM/YYYY') : 'N/A'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'gratification',
                            columnNameKey: 'Gratificación',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.gratification || 'N/A'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'comments',
                            columnNameKey: 'Observaciones',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.comments || 'N/A'}
                                    </div>
                                );
                            }
                        },
                        /*
                        {
                            key: 'drumsNumber',
                            columnNameKey: 'Número de bidones',
                            render: (row: any) => {
                                return (
                                    <span className="flex flex-nowrap gap-2 capitalize items-center">
                                        <GiOilDrum className="text-gray-400 min-h-[15px] min-w-[15px]" />
                                        {row.row.original?.drumsNumber}
                                    </span>
                                );
                            }
                        },
                        */
                        {
                            key: 'pickBetweenDays',
                            columnNameKey: 'Días de recogida',
                            size: 350,
                            render: (row: any) => {
                                return (
                                    <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                                        <div>Días: {row.row.original?.pickBetweenDays ?? 'N/A'}</div>
                                        <div>Horario: {row.row.original?.pickupSchedule ?? 'N/A'}</div>
                                        <div>Descanso: {row.row.original?.dayOff ?? 'N/A'}</div>
                                        <div>Inicio del servicio: {row.row.original?.startService?.date ? moment(row.row.original?.startService?.date).format('DD/MM/YYYY') : 'N/A'}</div>
                                        <div>Fin del servicio: {row.row.original?.endService?.date ? moment(row.row.original?.endService?.date).format('DD/MM/YYYY') : 'N/A'}</div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'highSeason',
                            columnNameKey: 'Temporada alta',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.highSeason ? 'Sí' : 'No'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'provider',
                            columnNameKey: 'Proveedor',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.provider?.companyName}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'active',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original.active ? 'Desactivar' : 'Activar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            id={row.row.original.id}
                                            checked={row.row.original.active}
                                            disabled={changingStatus.includes(row.row.original.id)}
                                            onChange={() => toggleStatus(row.row.original.id, row.row.original.active)}
                                            switchKnobClassName='bg-secondary'
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => {
                                const date = row.row.original?.createdAt?.date;
                                return date ? moment(date).format('DD/MM/YYYY') : 'N/A';
                            },
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'headquarter', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver perfil'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.headquarters.path}/${row.id}/profile/${menuRoutes.headquarters.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'headquarter', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar sede'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setHeadquarterSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ProviderApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <HeadquarterDeleteAllModal isOpen={openDeleteAllModal} onClose={() => setOpenDeleteAllModal(false)} refetch={refetch} />

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Sede'} onClear={() => resetFilters(50)}>
                <HeadquartersFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    headquarterSelected ?
                        "Completa el formulario para editar los datos de la sede" :
                        "Completa el formulario para crear una nueva sede"
                }
                drawerTitle={headquarterSelected ? 'Editar Sede' : 'Crear Sede'} submitText={headquarterSelected ? 'Guardar Sede' : 'Crear Sede'}>
                <HeadquarterForm setOpenModal={setOpenFormDrawer} headquarter={headquarterSelected} refetch={refetch} />
            </FormDrawerView>

        </Page>
    );
};

export default HeadquartersList;
